import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import type { DataTableColumnDef } from '@repo/ui/data-table';
import { DataTable } from '@repo/ui/data-table';
import { DataTablePagination } from '@repo/ui/data-table-pagination';
import { formatDate } from '@repo/ui/lib/formatters';
import { Skeleton } from '@repo/ui/skeleton';
import { TableCell } from '@repo/ui/table';
import { parseAsInteger, useQueryStates } from 'nuqs';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { trpc } from '@/providers/trpc';
import { SettingsSecurityPasskeyTableActions } from './passkey-table-actions';

export const SettingsSecurityPasskeyTable = () => {
  useLocale();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    perPage: parseAsInteger.withDefault(10),
  });

  const { data, isLoading, isLoadingError } = trpc.auth.listPasskeys.useQuery(
    {
      page: searchParams.page,
      perPage: searchParams.perPage,
    },
    {
      placeholderData: (previousData) => previousData,
    },
  );

  const onPaginationChange = (page: number, perPage: number) => {
    setSearchParams({
      page,
      perPage,
    });
  };

  const results = data ?? {
    data: [],
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  };

  const columns = useMemo(() => {
    return [
      {
        get header() {
          return t('workspace.name');
        },
        accessorKey: 'name',
      },
      {
        get header() {
          return t('workspace.created');
        },
        accessorKey: 'createdAt',
        cell: ({ row }) => formatDate(row.original.createdAt),
      },

      {
        get header() {
          return t('workspace.lastUsed');
        },
        accessorKey: 'updatedAt',
        cell: ({ row }) =>
          row.original.lastUsedAt ? formatDate(row.original.lastUsedAt) : t('extra.never'),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <SettingsSecurityPasskeyTableActions
            className="justify-end"
            passkeyId={row.original.id}
            passkeyName={row.original.name}
          />
        ),
      },
    ] satisfies DataTableColumnDef<(typeof results)['data'][number]>[];
  }, [getLocale()]);

  return (
    <DataTable
      columns={columns}
      currentPage={results.page}
      data={results.data}
      error={{
        enable: isLoadingError,
      }}
      hasFilters={searchParams.page !== undefined || searchParams.perPage !== undefined}
      onClearFilters={async () => navigate(pathname ?? '/')}
      onPaginationChange={onPaginationChange}
      perPage={results.perPage}
      skeleton={{
        enable: isLoading,
        rows: 3,
        component: (
          <>
            <TableCell>
              <Skeleton className="h-4 w-20 rounded-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-12 rounded-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-12 rounded-full" />
            </TableCell>
            <TableCell>
              <div className="flex flex-row space-x-2">
                <Skeleton className="h-8 w-16 rounded" />
                <Skeleton className="h-8 w-16 rounded" />
              </div>
            </TableCell>
          </>
        ),
      }}
      totalPages={results.totalPages}
    >
      {(table) => <DataTablePagination table={table} />}
    </DataTable>
  );
};
